import { ReactComponent as RentersPolicyIcon } from '@@/assets/toyota/image/building.svg';
import builtOnSureLogo from '@@/assets/toyota/image/built-on-sure.svg';
import { ReactComponent as CreditCardIcon } from '@@/assets/toyota/image/card.svg';
import { ReactComponent as ToyotaCaretIcon } from '@@/assets/toyota/image/caret.svg';
import { ReactComponent as TenantVehicleIcon } from '@@/assets/toyota/image/corolla.svg';
import idCardsIconSrc from '@@/assets/toyota/image/document-multiple.png';
import policyIconSrc from '@@/assets/toyota/image/document-single.png';
import errorImg from '@@/assets/toyota/image/errorImg.svg';
import { ReactComponent as IdIcon } from '@@/assets/toyota/image/id_card_icon.svg';
import { ReactComponent as TenantInfoIconSolid } from '@@/assets/toyota/image/info-icon.svg';
import peopleLaughingImgPng from '@@/assets/toyota/image/people-laughing.png';
import peopleLaughingImgWebP from '@@/assets/toyota/image/people-laughing.webp';
import { ReactComponent as TenantAlertIcon } from '@@/assets/toyota/image/red-alert.svg';
import selfServiceHero from '@@/assets/toyota/image/self-service-hero.min.svg';
import { ReactComponent as RoadsideAssistanceIcon } from '@@/assets/toyota/image/tow_truck_icon.svg';
import toyotaProtectAssetsImg from '@@/assets/toyota/image/toyota-assets.svg';
import tenantDesktopBuckleUpImg from '@@/assets/toyota/image/toyota-buckle-up-desktop.svg';
import tenantMobileBuckleUpImg from '@@/assets/toyota/image/toyota-buckle-up-mobile.svg';
import { ReactComponent as TenantCarIcon } from '@@/assets/toyota/image/toyota-car-icon.svg';
import { ReactComponent as SuccessIcon } from '@@/assets/toyota/image/toyota-checkmark.svg';
import { ReactComponent as TenantDriverIcon } from '@@/assets/toyota/image/toyota-driver-icon.v2.svg';
import tenantCityImg from '@@/assets/toyota/image/toyota-little-city.svg';
import { ReactComponent as ToyotaLogo } from '@@/assets/toyota/image/toyota-logo.svg';
import mfaBaseImage from '@@/assets/toyota/image/toyota-mfa-base.svg';
import mfaImage from '@@/assets/toyota/image/toyota-mfa.svg';
import toyotaProtectLovedImg from '@@/assets/toyota/image/toyota-protect-you-and-loved-ones.svg';
import toyotaProtectVehicleImg from '@@/assets/toyota/image/toyota-vehicle.svg';
import ToyotaFooterHeader from '@@/components/Footer/headers/ToyotaFooterHeader';
import KnockoutCopy from '@@/components/KnockoutCopy/KnockoutCopy';
import Text from '@@/components/LayoutContent/Text';
import ToyotaHero from '@@/components/LayoutSignUp/heroes/ToyotaHero';
import { TNavLinkWLConfigOptions } from '@@/components/NavBar/useNavLinks';
import { TConfig, TGetAddOns } from '@@/interfaces';
import { prepUrlForEnvironment } from '@@/utils';
import { excludeFalse } from '@@/utils/excludeFalse';

const mfaSuccessHost = prepUrlForEnvironment({
  url: process.env.REACT_APP_MFA_SUCCESS_REDIRECT_HOST,
  shouldUseCurrentHost: (hostname: string) => hostname === 'localhost',
});

const toyotaAutoInsuranceUrl = 'https://toyotaims.com/toyotaautoinsurance';

const config: DeepPartial<TConfig> = {
  accidentsAndViolationsViewConfig: {
    cancelButtonLabel: 'Back Up',
    nextButtonLabel: 'Drive On',
  },
  auto: {
    addOns: {
      getAddOns: ({ userAction, userFlow }: Parameters<TGetAddOns>[0]) =>
        excludeFalse([
          'towing_road_services',
          'temporary_ride',
          'side_hustle',
          userFlow === 'self-service' && 'gap',
          'oem_parts',
          'new_car_replacement',
          userAction === 'editing-addons' && 'pet_passenger',
        ] as const),
    },
  },
  addonModalConfig: {
    carbonOffsetDescriptionCopy: '',
  },
  brazeConfig: {
    isBrazeEnabled: false,
  },
  fetchingViewConfig: {
    defaultAdpfHeader: 'Hang here for just a minute.',
    defaultSubheader: "We're personalizing your quote...",
    tenantDesktopImg: tenantDesktopBuckleUpImg,
    tenantMobileImg: tenantMobileBuckleUpImg,
  },
  cmsSlugs: {
    authenticatedFooterSlug: 'toyota-footer',
    copyrightSlug: 'toyota-copyright',
    footerSlug: 'toyota-footer',
    privacyPolicySlug: 'toyota-privacy-policy',
    termsViewSlug: 'toyota-terms-and-conditions',
  },
  coverage: {
    compColl: {
      modalContent:
        'Comprehensive pays for accidental damage to your car from things other than a collision - like floods, hail, fire, theft, and windshield or animal damage minus your chosen deductible. Collision pays for accidental damage to your car caused by collision with another vehicle or fixed object minus your chosen deductible. Deductibles chosen are per vehicle.',
    },
  },
  coverageConfig: {
    subHeader: `To start, we've made selections based on your current policy. Adjust items to get the exact coverage you want. Or, if everything looks good, just skip to the next step.`,
    getOverviewLineTextVariant: (isAddOn: boolean) =>
      isAddOn ? 'body2' : 'h5',
    tenantAssetsImg: toyotaProtectAssetsImg,
    tenantLoveImg: toyotaProtectLovedImg,
    tenantVehicleImg: toyotaProtectVehicleImg,
  },
  coverageStartDateConfig: {
    infoCopy: null,
    labelCopy: 'Select a start date',
    nextButtonCopy: 'Drive on',
    cancelButtonCopy: 'Back up',
  },
  cssFiles: ['toyota.css'],
  datePickerConfig: {
    placeholder: 'mm/dd/yyyy',
  },
  discountsConfig: {
    hasCarbonOffsetDiscount: false,
    hasPartnerDiscount: true,
    partnerDiscountDescription:
      'You are eligible for this discount because Toyota Insurance Management Solutions is an affinity partner of Toggle, a member of the Farmers Insurance family and the underwriter of Toyota Auto Insurance.',
  },
  editPolicyReviewConfig: { editButtonVariant: 'ACTION' },
  errorPageConfig: {
    backgroundImgSrc: errorImg,
  },
  excludeDriversViewConfig: {
    submitButtonLabel: 'Drive On',
  },
  excludeVehicleViewConfig: {
    submitButtonLabel: 'Drive On',
  },
  featureFlags: {
    isAccountManagementEnabled: true,
    isAutoPurchaseFlowEnabled: true,
    isAutoQuoteLinkExchangeEnabled: true,
    isDirectToAutoQuoteEnabled: true,
    isLogoRedirectInAutoPurchaseFlowEnabled: true,
    isPrivacyAndTermsEnabled: true,
    isRentersQuoteCreationEnabled: false,
    isSelfServiceEnabled: true,
  },
  footerConfig: {
    hasNavItems: false,
    hasSocialLinks: false,
    header: <ToyotaFooterHeader logo={<ToyotaLogo />} />,
    sureFooterLogoSrc: builtOnSureLogo,
    tenantPhoneVariant: 'second-row',
  },
  icons: {
    CaretIcon: ToyotaCaretIcon,
    SuccessIcon,
    TenantAlertIcon,
    TenantCarIcon,
    TenantDriverIcon,
    TenantInfoIconSolid,
    TenantVehicleIcon,
    CreditCardIcon,
    autoPolicyIcons: {
      AutoPolicyIcon: TenantVehicleIcon,
      IdIcon,
      RoadsideAssistanceIcon,
      idCardsIconSrc,
      policyIconSrc,
    },
    rentersPolicyIcons: {
      RentersPolicyIcon,
    },
    trustIcons: [
      'pf-visa',
      'pf-mastercard-alt',
      'pf-american-express-alt',
      'pf-discover',
    ],
  },
  iidKnockoutCopy: {
    featureFlag: 'isUpdatedToyotaKnockoutCopy',
    subHeader: <KnockoutCopy phoneNumber="888-492-1780" />,
  },
  ineligibleAddressConfig: {
    getCancellationCopy: ({
      oldState,
      date,
    }: {
      oldState?: string;
      date?: string;
    }) => (
      <>
        Okay, your {oldState} cancellation date will be today,{' '}
        <strong>{date}</strong>.
      </>
    ),
  },
  layoutContentConfig: {
    tenantCityImg,
  },
  layoutQuoteConfig: {
    isLogoHeader: false,
    summaryPosition: 'sticky',
    variant: '100vw-footer',
    summaryCopy: 'Your current auto insurance quote:',
  },
  layoutSelfServiceConfig: {
    backgroundImgSrc: selfServiceHero,
  },
  localforageDbName: 'toyota',
  loginConfig: {
    backgroundImgSrc: peopleLaughingImgPng,
  },
  logos: {
    default: ToyotaLogo,
    light: ToyotaLogo,
    title: 'Toyota | Toggle',
  },
  mfaConfig: {
    mfaImage,
    mfaBaseImage,
  },
  missingLicenseViewConfig: {
    submitButtonLabel: 'Drive On',
  },
  missingLicenseInfoViewConfig: {
    submitButtonLabel: 'Drive On',
  },
  missingLienholderViewConfig: {
    submitButtonLabel: 'Drive On',
  },
  missingVinViewConfig: {
    submitButtonLabel: 'Drive On',
  },
  navBarConfig: {
    getLinks: ({ isLoggedIn }: TNavLinkWLConfigOptions) => {
      const loggedInMenuLinks = [
        {
          to: '/my-account',
          title: 'My policies',
          dataTestId: 'policies',
        },
        {
          to: '/my-activity',
          title: 'My payments',
          dataTestId: 'payments',
        },
        {
          to: '/my-profile',
          title: 'My profile',
          dataTestId: 'profile',
        },
        {
          to: process.env.REACT_APP_CLAIMS_URL!,
          title: 'Claims',
          dataTestId: 'claims',
        },
        {
          to: process.env.REACT_APP_FAQ_URL!,
          title: 'FAQs',
          dataTestId: 'faqs',
        },
      ];

      const loggedInFooterLinks = [
        { to: process.env.REACT_APP_ABOUT_US_URL!, title: 'About Us' },
        { to: '/terms', title: 'Terms' },
        { to: '/privacy', title: 'Privacy' },
      ];

      return {
        main: isLoggedIn ? loggedInMenuLinks : [],
        footer: isLoggedIn ? loggedInFooterLinks : [],
        shouldHidePhoneLink: true,
        shouldHideSocialLinks: true,
        loginLogout: isLoggedIn ? { to: '/logout', title: 'Log out' } : null,
      };
    },
  },
  noAutoCoverageConfig: {
    ctaSubmitCopy: `Thanks! We’ll be in touch as soon as Toyota Auto Insurance is available in your state.`,
    getNoCoverageCopy: ({ regionName }: { regionName: string }) => (
      <>
        {`Toyota Auto Insurance is not in ${regionName} yet, but we can still help
        you, call `}
        <a href="tel:8772490086">877-249-0086</a>.
      </>
    ),
    secondaryCopy: 'Let me know when Toyota Auto comes my way!',
  },
  notFoundPageConfig: {
    backgroundImgSrc: '',
    ctaHref: 'https://toyotaautoinsurance.com',
  },
  notifyConfig: {
    discountCopy:
      'Congratulations! Being a Toyota customer unlocks a discount and exclusive features.',
  },
  partnerCode: 'toggle_toyota',
  passwordValidator: {
    passingIconType: 'circle-fill',
  },
  primaryVehicleDataSource: 'toyota',
  privacyPolicyView: {
    insuranceUrl: 'app.toyotaautoinsurance.com',
  },
  purchaseFlowConfig: {
    checkoutExistingToggleCustomerAlreadyLoggedInCopy:
      "Looks like you're already a Toggle renters customer. Nice!",
    checkoutExistingToggleCustomerLoginSuccessCopy:
      'Great! As an existing Toggle customer, you may be eligible for an additional discount.',
    checkoutExistingToggleCustomerPrompt: (
      <>
        <Text variant="inherit">Do you have Toggle® renters insurance?</Text>
        <Text variant="inherit">
          (Toyota Auto Insurance is powered by Toggle, a member of the Farmers®
          Insurance family.)
        </Text>
      </>
    ),
    checkoutToggleCustomerDefault: false,
  },
  quoteExpirationCondition: {
    maxElapsedTimeMinutes: 60,
    onConfirmDestination: toyotaAutoInsuranceUrl,
    timestampKey: 'updated_at',
  },
  redirectUrls: {
    indexRedirectUrl: toyotaAutoInsuranceUrl,
    mfaSuccessRedirectUrl: `${mfaSuccessHost}my-account`,
    isPartnerRedirectUrlEnabled: true,
  },
  reviewAutoQuoteConfig: {
    nextButtonCopy: 'Next Stop: Signatures',
  },
  signUpViewConfig: {
    backgroundPicture: {
      alt: 'Young adults laughing',
      sources: [
        {
          src: peopleLaughingImgWebP,
          type: 'image/webp',
        },
        {
          src: peopleLaughingImgPng,
          type: 'image/png',
        },
      ],
      fallbackSrc: peopleLaughingImgPng,
    },
    heroVariant: 'above-form',
    Hero: ToyotaHero,
    title: 'Cover Your Stuff | Toyota Insurance',
    copy: {
      header:
        "Customize your policy and never pay for coverage you don't want.",
      hero1: 'Toyota Auto Insurance',
      hero2: 'Protecting your journey, wherever the road takes you.',
      informationalHeader: 'But first...',
      informationalSubHeader: 'Confirm the details below.',
    },
    hasMainNav: false,
  },
  stripeElStyles: {
    base: {
      fontFamily: "'Toyota Type', Helvetica, sans-serif",
      fontSize: '14px',
      color: '#696969',
      '::placeholder': {
        color: '#787878',
      },
    },
    fonts: [
      {
        family: 'Toyota Type',
        // NOTE: this using relative URLs here causes issues with localhost (need https for non-mixed content), and e2ws, sandbox, and QA since they're password protected
        src: `url(https://sure-host.s3.amazonaws.com/static/toyota/ToyotaType-Regular.ttf) format('truetype')`,
      },
    ],
    invalid: {
      iconColor: '#e50c22',
      color: '#696969',
    },
  },
  tenantDisplayName: 'Toyota',
  tenantPhoneNumber: '1-888-492-1780',
  vehicleManagementCopyConfig: {
    subheader:
      "We've already included your Toyota. Feel free to add additional vehicles.",
  },
};

export default config;
